import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ClockIcon, MapPinIcon, XIcon, MinusIcon, PlusIcon, CheckCircleIcon, SmartphoneIcon, CreditCardIcon, MusicIcon, ArrowLeftIcon } from 'lucide-react';
interface Event {
  id: number;
  title: string;
  dj: string;
  day: string;
  month: string;
  date: string;
  time: string;
  venue: string;
  price: number;
  tag: string;
  gradient: string;
  image: string;
}
const events: Event[] = [{
  id: 1,
  title: 'FRIDAY NIGHT LIVE',
  dj: 'DJ MARCUS STEEL',
  day: '14',
  month: 'MAR',
  date: 'Friday, March 14',
  time: '10:00 PM — 3:00 AM',
  venue: 'MK Lounge DC — Main Floor',
  price: 35,
  tag: 'LIVE',
  gradient: 'linear-gradient(160deg, #0a0500 0%, #1a0800 35%, #2e1200 65%, #C9A84C1a 100%)',
  image: 'https://cdn.magicpatterns.com/uploads/1HxCUa6riEEckBjunctso5/Friday_NIGHT.webp'
}, {
  id: 2,
  title: 'SATURDAY TAKEOVER',
  dj: 'DJ NOVA & GUESTS',
  day: '15',
  month: 'MAR',
  date: 'Saturday, March 15',
  time: '11:00 PM — 4:00 AM',
  venue: 'MK Lounge DC — VIP Level',
  price: 45,
  tag: 'VIP',
  gradient: 'linear-gradient(160deg, #05000f 0%, #0d0020 35%, #1a0035 65%, #C9A84C18 100%)',
  image: 'https://cdn.magicpatterns.com/uploads/fnP18MYqsMc6eEJasSpkrM/MK_LOUNGE.png'
}, {
  id: 3,
  title: 'LATIN NIGHTS',
  dj: 'DJ CALIENTE',
  day: '21',
  month: 'MAR',
  date: 'Friday, March 21',
  time: '9:00 PM — 2:00 AM',
  venue: 'MK Lounge DC — All Floors',
  price: 30,
  tag: 'HOT',
  gradient: 'linear-gradient(160deg, #0a0500 0%, #201000 35%, #3a1800 65%, #E8C97A1a 100%)',
  image: 'https://cdn.magicpatterns.com/uploads/pwVKT3ydjMFRMAJ8WUZEdP/DC_NIGHT_LIFE.png'
}, {
  id: 4,
  title: 'INDUSTRY SUNDAYS',
  dj: 'RESIDENT DJS',
  day: '23',
  month: 'MAR',
  date: 'Sunday, March 23',
  time: '8:00 PM — 1:00 AM',
  venue: 'MK Lounge DC — Skybox',
  price: 25,
  tag: 'INDUSTRY',
  gradient: 'linear-gradient(160deg, #050505 0%, #0f0f00 35%, #1c1c00 65%, #C9A84C1c 100%)',
  image: 'https://cdn.magicpatterns.com/uploads/fnP18MYqsMc6eEJasSpkrM/MK_LOUNGE.png'
}];
type PaymentMethod = 'card' | 'cashapp' | null;
type CheckoutStep = 1 | 2 | 3;
interface BookingConfirmation {
  event: Event;
  quantity: number;
  total: number;
  confirmationCode: string;
  paymentMethod: PaymentMethod;
}
function QRCode({
  code
}: {
  code: string;
}) {
  // Deterministic QR-like pattern from confirmation code
  const size = 120;
  const cells = 11;
  const cellSize = size / cells;
  const pattern: boolean[][] = Array.from({
    length: cells
  }, (_, r) => Array.from({
    length: cells
  }, (_, c) => {
    // Corner finder patterns
    if (r < 3 && c < 3 || r < 3 && c >= cells - 3 || r >= cells - 3 && c < 3) return true;
    // Data cells based on code hash
    const hash = (code.charCodeAt(r % code.length) * 31 + c * 17 + r * 7) % 3;
    return hash === 0;
  }));
  return <div className="inline-block p-3 rounded-xl" style={{
    background: '#fff'
  }} data-id="element-693">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-id="element-694">
        {pattern.map((row, r) => row.map((filled, c) => filled ? <rect key={`${r}-${c}`} x={c * cellSize} y={r * cellSize} width={cellSize - 0.5} height={cellSize - 0.5} fill="#050505" rx="0.5" data-id="element-695" /> : null))}
      </svg>
    </div>;
}
function CheckoutModal({
  event,
  onClose
}: {
  event: Event;
  onClose: () => void;
}) {
  const [step, setStep] = useState<CheckoutStep>(1);
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [cardName, setCardName] = useState('');
  const [cardEmail, setCardEmail] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cashAppSent, setCashAppSent] = useState(false);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const total = event.price * quantity;
  const handleConfirmPayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      const code = `MK${Date.now().toString(36).toUpperCase().slice(-6)}`;
      setConfirmation({
        event,
        quantity,
        total,
        confirmationCode: code,
        paymentMethod
      });
      setStep(3);
      setIsProcessing(false);
    }, 1800);
  };
  const formatCard = (val: string) => val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 4);
    return digits.length >= 3 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-696">
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0" style={{
      background: 'rgba(5,5,5,0.88)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)'
    }} onClick={step !== 3 ? onClose : undefined} data-id="element-697" />

      {/* Modal */}
      <motion.div initial={{
      y: 60,
      opacity: 0,
      scale: 0.96
    }} animate={{
      y: 0,
      opacity: 1,
      scale: 1
    }} exit={{
      y: 60,
      opacity: 0,
      scale: 0.96
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 32
    }} className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col" style={{
      background: '#0d0b09',
      border: '1px solid #2A2622',
      boxShadow: '0 24px 80px rgba(0,0,0,0.8)',
      maxHeight: '92vh'
    }} data-id="element-698">
        {/* Close */}
        {step !== 3 && <button onClick={onClose} className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-[#2A2622] text-white/50 hover:text-[#C9A84C] hover:border-[#C9A84C]/40 transition-all" style={{
        background: 'rgba(5,5,5,0.7)'
      }} data-id="element-699">
            <XIcon size={14} data-id="element-700" />
          </button>}

        <AnimatePresence mode="wait" data-id="element-701">
          {/* STEP 1: Event detail + ticket selection */}
          {step === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.25
        }} className="flex flex-col overflow-y-auto" style={{
          maxHeight: '92vh'
        }} data-id="element-702">
              {/* Image header */}
              <div className="relative flex-shrink-0" style={{
            height: 180,
            background: event.gradient
          }} data-id="element-703">
                <div className="absolute inset-0 opacity-20" style={{
              background: 'radial-gradient(ellipse at 60% 40%, rgba(201,168,76,0.4) 0%, transparent 60%)'
            }} data-id="element-704" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0d0b09]" data-id="element-705" />
                <div className="absolute top-4 left-4 flex items-center gap-2 rounded-xl px-3 py-2" style={{
              background: '#C9A84C'
            }} data-id="element-706">
                  <CalendarIcon size={14} className="text-[#050505]" data-id="element-707" />
                  <div data-id="element-708">
                    <div className="font-montserrat font-black text-xl leading-none text-[#050505]" data-id="element-709">
                      {event.day}
                    </div>
                    <div className="font-montserrat text-[8px] tracking-widest text-[#050505]/80" data-id="element-710">
                      {event.month}
                    </div>
                  </div>
                </div>
                <div className="absolute top-4 right-12 border border-[#C9A84C]/50 rounded-lg px-2 py-1" data-id="element-711">
                  <span className="font-montserrat text-[9px] tracking-widest text-[#C9A84C]" data-id="element-712">
                    {event.tag}
                  </span>
                </div>
                <div className="absolute bottom-4 left-5" data-id="element-713">
                  <h2 className="font-montserrat font-black text-xl tracking-widest text-white" data-id="element-714">
                    {event.title}
                  </h2>
                  <p className="font-montserrat text-sm text-[#C9A84C] tracking-wider" data-id="element-715">
                    {event.dj}
                  </p>
                </div>
              </div>

              <div className="p-5 flex flex-col gap-4" data-id="element-716">
                {/* Event meta */}
                <div className="flex flex-col gap-2" data-id="element-717">
                  <div className="flex items-center gap-2 text-white/50" data-id="element-718">
                    <ClockIcon size={12} data-id="element-719" />
                    <span className="font-inter text-xs" data-id="element-720">{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/50" data-id="element-721">
                    <MapPinIcon size={12} data-id="element-722" />
                    <span className="font-inter text-xs" data-id="element-723">{event.venue}</span>
                  </div>
                </div>

                <div className="h-px bg-[#2A2622]" data-id="element-724" />

                {/* Ticket quantity */}
                <div data-id="element-725">
                  <p className="font-montserrat text-[10px] tracking-widest text-white/40 mb-3" data-id="element-726">
                    SELECT TICKETS
                  </p>
                  <div className="flex items-center justify-between" data-id="element-727">
                    <div className="flex items-center gap-4" data-id="element-728">
                      <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 rounded-xl border border-[#2A2622] flex items-center justify-center text-white/60 hover:border-[#C9A84C]/50 hover:text-[#C9A84C] transition-all" data-id="element-729">
                        <MinusIcon size={14} data-id="element-730" />
                      </button>
                      <span className="font-montserrat font-black text-2xl text-white w-6 text-center" data-id="element-731">
                        {quantity}
                      </span>
                      <button onClick={() => setQuantity(q => Math.min(10, q + 1))} className="w-9 h-9 rounded-xl border border-[#2A2622] flex items-center justify-center text-white/60 hover:border-[#C9A84C]/50 hover:text-[#C9A84C] transition-all" data-id="element-732">
                        <PlusIcon size={14} data-id="element-733" />
                      </button>
                    </div>
                    <div className="text-right" data-id="element-734">
                      <p className="font-inter text-xs text-white/40" data-id="element-735">
                        {quantity} × ${event.price}
                      </p>
                      <p className="font-montserrat font-black text-2xl text-[#C9A84C]" data-id="element-736">
                        ${total}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-[#2A2622]" data-id="element-737" />

                {/* Payment method selection */}
                <div data-id="element-738">
                  <p className="font-montserrat text-[10px] tracking-widest text-white/40 mb-3" data-id="element-739">
                    PAYMENT METHOD
                  </p>
                  <div className="grid grid-cols-2 gap-3" data-id="element-740">
                    <button onClick={() => setPaymentMethod('card')} className="flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all duration-200" style={{
                  border: paymentMethod === 'card' ? '1px solid rgba(201,168,76,0.6)' : '1px solid #2A2622',
                  background: paymentMethod === 'card' ? 'rgba(201,168,76,0.08)' : 'transparent'
                }} data-id="element-741">
                      <CreditCardIcon size={16} className={paymentMethod === 'card' ? 'text-[#C9A84C]' : 'text-white/40'} data-id="element-742" />
                      <span className={`font-montserrat text-xs tracking-widest ${paymentMethod === 'card' ? 'text-[#C9A84C]' : 'text-white/50'}`} data-id="element-743">
                        CARD
                      </span>
                    </button>
                    <button onClick={() => setPaymentMethod('cashapp')} className="flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all duration-200" style={{
                  border: paymentMethod === 'cashapp' ? '1px solid rgba(201,168,76,0.6)' : '1px solid #2A2622',
                  background: paymentMethod === 'cashapp' ? 'rgba(201,168,76,0.08)' : 'transparent'
                }} data-id="element-744">
                      <SmartphoneIcon size={16} className={paymentMethod === 'cashapp' ? 'text-[#C9A84C]' : 'text-white/40'} data-id="element-745" />
                      <span className={`font-montserrat text-xs tracking-widest ${paymentMethod === 'cashapp' ? 'text-[#C9A84C]' : 'text-white/50'}`} data-id="element-746">
                        CASH APP
                      </span>
                    </button>
                  </div>
                </div>

                <button onClick={() => paymentMethod && setStep(2)} disabled={!paymentMethod} className="w-full font-montserrat font-black text-xs tracking-widest py-4 rounded-xl transition-all duration-300" style={{
              background: paymentMethod ? '#C9A84C' : 'rgba(201,168,76,0.2)',
              color: paymentMethod ? '#050505' : 'rgba(201,168,76,0.4)',
              cursor: paymentMethod ? 'pointer' : 'not-allowed'
            }} data-id="element-747">
                  CONTINUE TO PAYMENT
                </button>
              </div>
            </motion.div>}

          {/* STEP 2: Payment */}
          {step === 2 && <motion.div key="step2" initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.25
        }} className="flex flex-col overflow-y-auto" style={{
          maxHeight: '92vh'
        }} data-id="element-748">
              {/* Header */}
              <div className="flex items-center gap-3 p-5 border-b border-[#2A2622]" data-id="element-749">
                <button onClick={() => setStep(1)} className="w-8 h-8 flex items-center justify-center rounded-xl border border-[#2A2622] text-white/50 hover:text-[#C9A84C] transition-colors" data-id="element-750">
                  <ArrowLeftIcon size={14} data-id="element-751" />
                </button>
                <div data-id="element-752">
                  <p className="font-montserrat text-[9px] tracking-widest text-white/30" data-id="element-753">
                    PAYMENT
                  </p>
                  <p className="font-montserrat font-black text-sm tracking-widest text-white" data-id="element-754">
                    {event.title}
                  </p>
                </div>
                <div className="ml-auto text-right" data-id="element-755">
                  <p className="font-inter text-[10px] text-white/30" data-id="element-756">
                    {quantity} ticket{quantity > 1 ? 's' : ''}
                  </p>
                  <p className="font-montserrat font-black text-lg text-[#C9A84C]" data-id="element-757">
                    ${total}
                  </p>
                </div>
              </div>

              <div className="p-5 flex flex-col gap-4" data-id="element-758">
                {paymentMethod === 'card' && <>
                    <p className="font-montserrat text-[10px] tracking-widest text-white/40" data-id="element-759">
                      CARD DETAILS
                    </p>

                    <div className="flex flex-col gap-3" data-id="element-760">
                      <div data-id="element-761">
                        <label className="font-inter text-[10px] text-white/40 mb-1 block" data-id="element-762">
                          FULL NAME
                        </label>
                        <input type="text" value={cardName} onChange={e => setCardName(e.target.value)} placeholder="John Smith" className="w-full bg-white/5 border border-[#2A2622] rounded-xl px-4 py-3 text-white/80 text-sm font-inter focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder:text-white/20" data-id="element-763" />
                      </div>
                      <div data-id="element-764">
                        <label className="font-inter text-[10px] text-white/40 mb-1 block" data-id="element-765">
                          EMAIL
                        </label>
                        <input type="email" value={cardEmail} onChange={e => setCardEmail(e.target.value)} placeholder="john@example.com" className="w-full bg-white/5 border border-[#2A2622] rounded-xl px-4 py-3 text-white/80 text-sm font-inter focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder:text-white/20" data-id="element-766" />
                      </div>
                      <div data-id="element-767">
                        <label className="font-inter text-[10px] text-white/40 mb-1 block" data-id="element-768">
                          CARD NUMBER
                        </label>
                        <input type="text" value={cardNumber} onChange={e => setCardNumber(formatCard(e.target.value))} placeholder="1234 5678 9012 3456" className="w-full bg-white/5 border border-[#2A2622] rounded-xl px-4 py-3 text-white/80 text-sm font-inter focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder:text-white/20 tracking-widest" data-id="element-769" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-770">
                        <div data-id="element-771">
                          <label className="font-inter text-[10px] text-white/40 mb-1 block" data-id="element-772">
                            EXPIRY
                          </label>
                          <input type="text" value={cardExpiry} onChange={e => setCardExpiry(formatExpiry(e.target.value))} placeholder="MM/YY" className="w-full bg-white/5 border border-[#2A2622] rounded-xl px-4 py-3 text-white/80 text-sm font-inter focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder:text-white/20" data-id="element-773" />
                        </div>
                        <div data-id="element-774">
                          <label className="font-inter text-[10px] text-white/40 mb-1 block" data-id="element-775">
                            CVV
                          </label>
                          <input type="text" value={cardCvv} onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="123" className="w-full bg-white/5 border border-[#2A2622] rounded-xl px-4 py-3 text-white/80 text-sm font-inter focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder:text-white/20" data-id="element-776" />
                        </div>
                      </div>
                    </div>

                    {/* Order summary */}
                    <div className="rounded-xl p-4" style={{
                background: 'rgba(201,168,76,0.06)',
                border: '1px solid rgba(201,168,76,0.15)'
              }} data-id="element-777">
                      <div className="flex justify-between mb-1" data-id="element-778">
                        <span className="font-inter text-xs text-white/50" data-id="element-779">
                          {quantity} × {event.title}
                        </span>
                        <span className="font-inter text-xs text-white/70" data-id="element-780">
                          ${total}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-781">
                        <span className="font-inter text-xs text-white/50" data-id="element-782">
                          Service fee
                        </span>
                        <span className="font-inter text-xs text-white/70" data-id="element-783">
                          $0
                        </span>
                      </div>
                      <div className="h-px bg-[#2A2622] my-2" data-id="element-784" />
                      <div className="flex justify-between" data-id="element-785">
                        <span className="font-montserrat font-black text-xs tracking-widest text-white" data-id="element-786">
                          TOTAL
                        </span>
                        <span className="font-montserrat font-black text-sm text-[#C9A84C]" data-id="element-787">
                          ${total}
                        </span>
                      </div>
                    </div>

                    <button onClick={handleConfirmPayment} disabled={isProcessing || !cardName || !cardEmail || cardNumber.length < 19 || cardExpiry.length < 5 || cardCvv.length < 3} className="w-full font-montserrat font-black text-xs tracking-widest py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2" style={{
                background: '#C9A84C',
                color: '#050505',
                opacity: isProcessing ? 0.7 : 1
              }} data-id="element-788">
                      {isProcessing ? <>
                          <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="w-4 h-4 border-2 border-[#050505]/30 border-t-[#050505] rounded-full" data-id="element-789" />
                          PROCESSING...
                        </> : `PAY $${total}`}
                    </button>
                  </>}

                {paymentMethod === 'cashapp' && <>
                    <p className="font-montserrat text-[10px] tracking-widest text-white/40" data-id="element-790">
                      CASH APP PAYMENT
                    </p>

                    <div className="flex flex-col items-center gap-4 py-2" data-id="element-791">
                      {/* CashApp branding */}
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{
                  background: '#00D632'
                }} data-id="element-792">
                        <span className="font-montserrat font-black text-2xl text-white" data-id="element-793">
                          $
                        </span>
                      </div>

                      <div className="text-center" data-id="element-794">
                        <p className="font-montserrat font-black text-2xl tracking-widest text-white mb-1" data-id="element-795">
                          $MKLoungedc
                        </p>
                        <p className="font-inter text-xs text-white/50" data-id="element-796">
                          Send exactly
                        </p>
                        <p className="font-montserrat font-black text-3xl text-[#C9A84C] mt-1" data-id="element-797">
                          ${total}
                        </p>
                      </div>

                      {/* QR placeholder for CashApp */}
                      <div className="p-3 rounded-2xl" style={{
                  background: '#fff'
                }} data-id="element-798">
                        <svg width="120" height="120" viewBox="0 0 120 120" data-id="element-799">
                          {/* Simplified CashApp QR pattern */}
                          {Array.from({
                      length: 11
                    }, (_, r) => Array.from({
                      length: 11
                    }, (_, c) => {
                      const isCorner = r < 3 && c < 3 || r < 3 && c >= 8 || r >= 8 && c < 3;
                      const isData = ((r + c + 7) * 13 + r * 5) % 4 === 0;
                      return isCorner || isData ? <rect key={`${r}-${c}`} x={c * 10.9 + 0.5} y={r * 10.9 + 0.5} width={9.5} height={9.5} fill="#050505" rx="0.8" data-id="element-800" /> : null;
                    }))}
                        </svg>
                      </div>

                      <div className="w-full rounded-xl p-4 text-center" style={{
                  background: 'rgba(0,214,50,0.08)',
                  border: '1px solid rgba(0,214,50,0.2)'
                }} data-id="element-801">
                        <p className="font-inter text-xs text-white/60 leading-relaxed" data-id="element-802">
                          Send{' '}
                          <span className="text-[#00D632] font-semibold" data-id="element-803">
                            ${total}
                          </span>{' '}
                          to{' '}
                          <span className="text-white font-semibold" data-id="element-804">
                            $MKLoungedc
                          </span>{' '}
                          with your event name in the note
                        </p>
                      </div>

                      <label className="flex items-center gap-3 cursor-pointer group" data-id="element-805">
                        <div onClick={() => setCashAppSent(s => !s)} className="w-5 h-5 rounded flex items-center justify-center transition-all duration-200 flex-shrink-0" style={{
                    background: cashAppSent ? '#C9A84C' : 'transparent',
                    border: cashAppSent ? '1px solid #C9A84C' : '1px solid #2A2622'
                  }} data-id="element-806">
                          {cashAppSent && <CheckCircleIcon size={12} className="text-[#050505]" data-id="element-807" />}
                        </div>
                        <span className="font-inter text-xs text-white/60" data-id="element-808">
                          I've sent the payment via Cash App
                        </span>
                      </label>
                    </div>

                    <button onClick={handleConfirmPayment} disabled={!cashAppSent || isProcessing} className="w-full font-montserrat font-black text-xs tracking-widest py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2" style={{
                background: cashAppSent ? '#C9A84C' : 'rgba(201,168,76,0.2)',
                color: cashAppSent ? '#050505' : 'rgba(201,168,76,0.4)',
                cursor: cashAppSent ? 'pointer' : 'not-allowed'
              }} data-id="element-809">
                      {isProcessing ? <>
                          <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="w-4 h-4 border-2 border-[#050505]/30 border-t-[#050505] rounded-full" data-id="element-810" />
                          CONFIRMING...
                        </> : 'CONFIRM BOOKING'}
                    </button>
                  </>}
              </div>
            </motion.div>}

          {/* STEP 3: Confirmation */}
          {step === 3 && confirmation && <motion.div key="step3" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col items-center overflow-y-auto" style={{
          maxHeight: '92vh'
        }} data-id="element-811">
              {/* Success header */}
              <div className="w-full relative flex-shrink-0" style={{
            height: 140,
            background: confirmation.event.gradient
          }} data-id="element-812">
                <div className="absolute inset-0 opacity-20" style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.5) 0%, transparent 70%)'
            }} data-id="element-813" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0d0b09]" data-id="element-814" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-815">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.2,
                type: 'spring',
                stiffness: 300,
                damping: 20
              }} className="w-16 h-16 rounded-full flex items-center justify-center" style={{
                background: '#C9A84C',
                boxShadow: '0 0 40px rgba(201,168,76,0.5)'
              }} data-id="element-816">
                    <CheckCircleIcon size={32} className="text-[#050505]" data-id="element-817" />
                  </motion.div>
                </div>
              </div>

              <div className="p-6 flex flex-col items-center gap-5 w-full" data-id="element-818">
                <div className="text-center" data-id="element-819">
                  <motion.h2 initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3
              }} className="font-montserrat font-black text-2xl tracking-widest text-white mb-1" data-id="element-820">
                    YOU'RE IN!
                  </motion.h2>
                  <motion.p initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4
              }} className="font-inter text-sm text-white/50" data-id="element-821">
                    Thank you for booking with MK Lounge DC
                  </motion.p>
                </div>

                {/* Booking details card */}
                <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.45
            }} className="w-full rounded-2xl p-4" style={{
              background: 'rgba(201,168,76,0.06)',
              border: '1px solid rgba(201,168,76,0.2)'
            }} data-id="element-822">
                  <div className="flex justify-between items-start mb-3" data-id="element-823">
                    <div data-id="element-824">
                      <p className="font-montserrat font-black text-sm tracking-widest text-white" data-id="element-825">
                        {confirmation.event.title}
                      </p>
                      <p className="font-montserrat text-xs text-[#C9A84C]" data-id="element-826">
                        {confirmation.event.dj}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-827">
                      <p className="font-inter text-[10px] text-white/40" data-id="element-828">
                        {confirmation.quantity} ticket
                        {confirmation.quantity > 1 ? 's' : ''}
                      </p>
                      <p className="font-montserrat font-black text-sm text-[#C9A84C]" data-id="element-829">
                        ${confirmation.total}
                      </p>
                    </div>
                  </div>
                  <div className="h-px bg-[#2A2622] mb-3" data-id="element-830" />
                  <div className="flex flex-col gap-1.5" data-id="element-831">
                    <div className="flex items-center gap-2 text-white/50" data-id="element-832">
                      <CalendarIcon size={11} className="text-[#C9A84C]" data-id="element-833" />
                      <span className="font-inter text-xs" data-id="element-834">
                        {confirmation.event.date}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-white/50" data-id="element-835">
                      <ClockIcon size={11} className="text-[#C9A84C]" data-id="element-836" />
                      <span className="font-inter text-xs" data-id="element-837">
                        {confirmation.event.time}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-white/50" data-id="element-838">
                      <MapPinIcon size={11} className="text-[#C9A84C]" data-id="element-839" />
                      <span className="font-inter text-xs" data-id="element-840">
                        {confirmation.event.venue}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* QR Code */}
                <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.55
            }} className="flex flex-col items-center gap-3" data-id="element-841">
                  <p className="font-montserrat text-[10px] tracking-widest text-white/40" data-id="element-842">
                    SCAN AT ENTRANCE
                  </p>
                  <QRCode code={confirmation.confirmationCode} data-id="element-843" />
                  <div className="text-center" data-id="element-844">
                    <p className="font-montserrat font-black text-lg tracking-[0.3em] text-[#C9A84C]" data-id="element-845">
                      {confirmation.confirmationCode}
                    </p>
                    <p className="font-inter text-[10px] text-white/30 mt-1" data-id="element-846">
                      Confirmation Code
                    </p>
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.65
            }} className="w-full rounded-xl p-3 text-center" style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid #2A2622'
            }} data-id="element-847">
                  <p className="font-inter text-xs text-white/40 leading-relaxed" data-id="element-848">
                    Present this QR code at the door. Screenshot or save this
                    page for easy access.
                  </p>
                </motion.div>

                <motion.button initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.7
            }} onClick={onClose} className="w-full font-montserrat font-black text-xs tracking-widest bg-[#C9A84C] text-[#050505] py-4 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" data-id="element-849">
                  DONE
                </motion.button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>
    </motion.div>;
}
export function EventsPage({
  onBack
}: {
  onBack?: () => void;
}) {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  return <section id="events" className="w-full bg-[#050505] min-h-screen pt-24 pb-20 px-4 sm:px-6" data-id="element-850">
      <div className="max-w-7xl mx-auto" data-id="element-851">
        {/* Page header with back button */}
        <div className="flex items-center gap-4 mb-10" data-id="element-852">
          {onBack && <motion.button initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4
        }} onClick={onBack} className="flex items-center gap-2 font-montserrat text-[10px] tracking-widest text-white/40 hover:text-[#C9A84C] transition-colors duration-300 group" data-id="element-853">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-854">
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-855" />
              </svg>
              BACK TO HOME
            </motion.button>}
        </div>

        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-856">
          <span className="font-inter text-[11px] tracking-[0.4em] text-[#C9A84C]/60 uppercase" data-id="element-857">
            What's On
          </span>
          <h2 className="font-montserrat font-black text-4xl sm:text-5xl tracking-widest text-white mt-2" data-id="element-858">
            UPCOMING EVENTS
          </h2>
          <div className="w-14 h-px bg-[#C9A84C]/50 mx-auto mt-4" data-id="element-859" />
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-860">
          {/* Card 1 */}
          <motion.article initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0
        }} className="flex flex-col rounded-2xl overflow-hidden cursor-pointer group" style={{
          background: '#0d0d0d',
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,168,76,0.04)',
          minHeight: 480
        }} data-id="element-861">
            <div className="relative flex-shrink-0" style={{
            height: 220,
            background: events[0].gradient
          }} data-id="element-862">
              <img src={events[0].image} alt={events[0].title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-500" data-id="element-863" />
              <div className="absolute inset-0 opacity-20" style={{
              background: 'radial-gradient(ellipse at 70% 30%, rgba(201,168,76,0.4) 0%, transparent 60%)'
            }} data-id="element-864" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0d0d0d]" data-id="element-865" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#C9A84C]/0 via-[#C9A84C]/0 to-[#C9A84C]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-866" />
              <div className="absolute top-4 left-4 flex items-center gap-2 rounded-xl px-3 py-2" style={{
              background: '#C9A84C',
              boxShadow: '0 4px 16px rgba(201,168,76,0.4)'
            }} data-id="element-867">
                <CalendarIcon size={14} className="text-[#050505]" data-id="element-868" />
                <div data-id="element-869">
                  <div className="font-montserrat font-black text-xl leading-none text-[#050505]" data-id="element-870">
                    14
                  </div>
                  <div className="font-montserrat text-[8px] tracking-widest text-[#050505]/80" data-id="element-871">
                    MAR
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 border border-[#C9A84C]/50 rounded-lg px-2 py-1" data-id="element-872">
                <span className="font-montserrat text-[9px] tracking-widest text-[#C9A84C]" data-id="element-873">
                  LIVE
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between" data-id="element-874">
                <MusicIcon size={36} className="text-[#C9A84C]/15" data-id="element-875" />
              </div>
            </div>
            <div className="flex flex-col flex-1 p-5" data-id="element-876">
              <h3 className="font-montserrat font-black text-base tracking-widest text-white leading-tight mb-1" data-id="element-877">
                FRIDAY NIGHT LIVE
              </h3>
              <p className="font-montserrat font-600 text-sm text-[#C9A84C] tracking-wider mb-3" data-id="element-878">
                DJ MARCUS STEEL
              </p>
              <div className="flex flex-col gap-1.5 mb-4" data-id="element-879">
                <div className="flex items-center gap-2 text-white/45" data-id="element-880">
                  <ClockIcon size={11} data-id="element-881" />
                  <span className="font-inter text-xs" data-id="element-882">10:00 PM — 3:00 AM</span>
                </div>
                <div className="flex items-center gap-2 text-white/45" data-id="element-883">
                  <MapPinIcon size={11} data-id="element-884" />
                  <span className="font-inter text-xs" data-id="element-885">Main Floor</span>
                </div>
              </div>
              <div className="mt-auto pt-3 border-t border-[#2A2622]" data-id="element-886">
                <div className="flex items-center justify-between mb-3" data-id="element-887">
                  <span className="font-inter text-xs text-white/40" data-id="element-888">
                    Per ticket
                  </span>
                  <span className="font-montserrat font-black text-lg text-[#C9A84C]" data-id="element-889">
                    $35
                  </span>
                </div>
                <button onClick={() => setSelectedEvent(events[0])} className="w-full font-montserrat font-black text-xs tracking-widest bg-[#C9A84C] text-[#050505] py-3.5 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
                boxShadow: '0 4px 20px rgba(201,168,76,0.25)'
              }} data-id="element-890">
                  BUY TICKETS
                </button>
              </div>
            </div>
          </motion.article>

          {/* Card 2 */}
          <motion.article initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.1
        }} className="flex flex-col rounded-2xl overflow-hidden cursor-pointer group" style={{
          background: '#0d0d0d',
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,168,76,0.04)',
          minHeight: 480
        }} data-id="element-891">
            <div className="relative flex-shrink-0" style={{
            height: 220,
            background: events[1].gradient
          }} data-id="element-892">
              <img src={events[1].image} alt={events[1].title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-500" data-id="element-893" />
              <div className="absolute inset-0 opacity-20" style={{
              background: 'radial-gradient(ellipse at 30% 60%, rgba(201,168,76,0.35) 0%, transparent 60%)'
            }} data-id="element-894" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0d0d0d]" data-id="element-895" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#C9A84C]/0 via-[#C9A84C]/0 to-[#C9A84C]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-896" />
              <div className="absolute top-4 left-4 flex items-center gap-2 rounded-xl px-3 py-2" style={{
              background: '#C9A84C',
              boxShadow: '0 4px 16px rgba(201,168,76,0.4)'
            }} data-id="element-897">
                <CalendarIcon size={14} className="text-[#050505]" data-id="element-898" />
                <div data-id="element-899">
                  <div className="font-montserrat font-black text-xl leading-none text-[#050505]" data-id="element-900">
                    15
                  </div>
                  <div className="font-montserrat text-[8px] tracking-widest text-[#050505]/80" data-id="element-901">
                    MAR
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 border border-[#C9A84C]/50 rounded-lg px-2 py-1" data-id="element-902">
                <span className="font-montserrat text-[9px] tracking-widest text-[#C9A84C]" data-id="element-903">
                  VIP
                </span>
              </div>
              <div className="absolute bottom-4 left-4" data-id="element-904">
                <MusicIcon size={36} className="text-[#C9A84C]/15" data-id="element-905" />
              </div>
            </div>
            <div className="flex flex-col flex-1 p-5" data-id="element-906">
              <h3 className="font-montserrat font-black text-base tracking-widest text-white leading-tight mb-1" data-id="element-907">
                SATURDAY TAKEOVER
              </h3>
              <p className="font-montserrat font-600 text-sm text-[#C9A84C] tracking-wider mb-3" data-id="element-908">
                DJ NOVA & GUESTS
              </p>
              <div className="flex flex-col gap-1.5 mb-4" data-id="element-909">
                <div className="flex items-center gap-2 text-white/45" data-id="element-910">
                  <ClockIcon size={11} data-id="element-911" />
                  <span className="font-inter text-xs" data-id="element-912">11:00 PM — 4:00 AM</span>
                </div>
                <div className="flex items-center gap-2 text-white/45" data-id="element-913">
                  <MapPinIcon size={11} data-id="element-914" />
                  <span className="font-inter text-xs" data-id="element-915">VIP Level</span>
                </div>
              </div>
              <div className="mt-auto pt-3 border-t border-[#2A2622]" data-id="element-916">
                <div className="flex items-center justify-between mb-3" data-id="element-917">
                  <span className="font-inter text-xs text-white/40" data-id="element-918">
                    Per ticket
                  </span>
                  <span className="font-montserrat font-black text-lg text-[#C9A84C]" data-id="element-919">
                    $45
                  </span>
                </div>
                <button onClick={() => setSelectedEvent(events[1])} className="w-full font-montserrat font-black text-xs tracking-widest bg-[#C9A84C] text-[#050505] py-3.5 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
                boxShadow: '0 4px 20px rgba(201,168,76,0.25)'
              }} data-id="element-920">
                  BUY TICKETS
                </button>
              </div>
            </div>
          </motion.article>

          {/* Card 3 */}
          <motion.article initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} className="flex flex-col rounded-2xl overflow-hidden cursor-pointer group" style={{
          background: '#0d0d0d',
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,168,76,0.04)',
          minHeight: 480
        }} data-id="element-921">
            <div className="relative flex-shrink-0" style={{
            height: 220,
            background: events[2].gradient
          }} data-id="element-922">
              <img src={events[2].image} alt={events[2].title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-500" data-id="element-923" />
              <div className="absolute inset-0 opacity-25" style={{
              background: 'radial-gradient(ellipse at 60% 40%, rgba(232,201,122,0.4) 0%, transparent 55%)'
            }} data-id="element-924" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0d0d0d]" data-id="element-925" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#C9A84C]/0 via-[#C9A84C]/0 to-[#C9A84C]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-926" />
              <div className="absolute top-4 left-4 flex items-center gap-2 rounded-xl px-3 py-2" style={{
              background: '#C9A84C',
              boxShadow: '0 4px 16px rgba(201,168,76,0.4)'
            }} data-id="element-927">
                <CalendarIcon size={14} className="text-[#050505]" data-id="element-928" />
                <div data-id="element-929">
                  <div className="font-montserrat font-black text-xl leading-none text-[#050505]" data-id="element-930">
                    21
                  </div>
                  <div className="font-montserrat text-[8px] tracking-widest text-[#050505]/80" data-id="element-931">
                    MAR
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 border border-[#C9A84C]/50 rounded-lg px-2 py-1" data-id="element-932">
                <span className="font-montserrat text-[9px] tracking-widest text-[#C9A84C]" data-id="element-933">
                  HOT
                </span>
              </div>
              <div className="absolute bottom-4 left-4" data-id="element-934">
                <MusicIcon size={36} className="text-[#C9A84C]/15" data-id="element-935" />
              </div>
            </div>
            <div className="flex flex-col flex-1 p-5" data-id="element-936">
              <h3 className="font-montserrat font-black text-base tracking-widest text-white leading-tight mb-1" data-id="element-937">
                LATIN NIGHTS
              </h3>
              <p className="font-montserrat font-600 text-sm text-[#C9A84C] tracking-wider mb-3" data-id="element-938">
                DJ CALIENTE
              </p>
              <div className="flex flex-col gap-1.5 mb-4" data-id="element-939">
                <div className="flex items-center gap-2 text-white/45" data-id="element-940">
                  <ClockIcon size={11} data-id="element-941" />
                  <span className="font-inter text-xs" data-id="element-942">9:00 PM — 2:00 AM</span>
                </div>
                <div className="flex items-center gap-2 text-white/45" data-id="element-943">
                  <MapPinIcon size={11} data-id="element-944" />
                  <span className="font-inter text-xs" data-id="element-945">All Floors</span>
                </div>
              </div>
              <div className="mt-auto pt-3 border-t border-[#2A2622]" data-id="element-946">
                <div className="flex items-center justify-between mb-3" data-id="element-947">
                  <span className="font-inter text-xs text-white/40" data-id="element-948">
                    Per ticket
                  </span>
                  <span className="font-montserrat font-black text-lg text-[#C9A84C]" data-id="element-949">
                    $30
                  </span>
                </div>
                <button onClick={() => setSelectedEvent(events[2])} className="w-full font-montserrat font-black text-xs tracking-widest bg-[#C9A84C] text-[#050505] py-3.5 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
                boxShadow: '0 4px 20px rgba(201,168,76,0.25)'
              }} data-id="element-950">
                  BUY TICKETS
                </button>
              </div>
            </div>
          </motion.article>

          {/* Card 4 */}
          <motion.article initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.3
        }} className="flex flex-col rounded-2xl overflow-hidden cursor-pointer group" style={{
          background: '#0d0d0d',
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,168,76,0.04)',
          minHeight: 480
        }} data-id="element-951">
            <div className="relative flex-shrink-0" style={{
            height: 220,
            background: events[3].gradient
          }} data-id="element-952">
              <img src={events[3].image} alt={events[3].title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-500" data-id="element-953" />
              <div className="absolute inset-0 opacity-20" style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.3) 0%, transparent 60%)'
            }} data-id="element-954" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0d0d0d]" data-id="element-955" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#C9A84C]/0 via-[#C9A84C]/0 to-[#C9A84C]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-956" />
              <div className="absolute top-4 left-4 flex items-center gap-2 rounded-xl px-3 py-2" style={{
              background: '#C9A84C',
              boxShadow: '0 4px 16px rgba(201,168,76,0.4)'
            }} data-id="element-957">
                <CalendarIcon size={14} className="text-[#050505]" data-id="element-958" />
                <div data-id="element-959">
                  <div className="font-montserrat font-black text-xl leading-none text-[#050505]" data-id="element-960">
                    23
                  </div>
                  <div className="font-montserrat text-[8px] tracking-widest text-[#050505]/80" data-id="element-961">
                    MAR
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 border border-[#C9A84C]/50 rounded-lg px-2 py-1" data-id="element-962">
                <span className="font-montserrat text-[9px] tracking-widest text-[#C9A84C]" data-id="element-963">
                  INDUSTRY
                </span>
              </div>
              <div className="absolute bottom-4 left-4" data-id="element-964">
                <MusicIcon size={36} className="text-[#C9A84C]/15" data-id="element-965" />
              </div>
            </div>
            <div className="flex flex-col flex-1 p-5" data-id="element-966">
              <h3 className="font-montserrat font-black text-base tracking-widest text-white leading-tight mb-1" data-id="element-967">
                INDUSTRY SUNDAYS
              </h3>
              <p className="font-montserrat font-600 text-sm text-[#C9A84C] tracking-wider mb-3" data-id="element-968">
                RESIDENT DJS
              </p>
              <div className="flex flex-col gap-1.5 mb-4" data-id="element-969">
                <div className="flex items-center gap-2 text-white/45" data-id="element-970">
                  <ClockIcon size={11} data-id="element-971" />
                  <span className="font-inter text-xs" data-id="element-972">8:00 PM — 1:00 AM</span>
                </div>
                <div className="flex items-center gap-2 text-white/45" data-id="element-973">
                  <MapPinIcon size={11} data-id="element-974" />
                  <span className="font-inter text-xs" data-id="element-975">Skybox</span>
                </div>
              </div>
              <div className="mt-auto pt-3 border-t border-[#2A2622]" data-id="element-976">
                <div className="flex items-center justify-between mb-3" data-id="element-977">
                  <span className="font-inter text-xs text-white/40" data-id="element-978">
                    Per ticket
                  </span>
                  <span className="font-montserrat font-black text-lg text-[#C9A84C]" data-id="element-979">
                    $25
                  </span>
                </div>
                <button onClick={() => setSelectedEvent(events[3])} className="w-full font-montserrat font-black text-xs tracking-widest bg-[#C9A84C] text-[#050505] py-3.5 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
                boxShadow: '0 4px 20px rgba(201,168,76,0.25)'
              }} data-id="element-980">
                  BUY TICKETS
                </button>
              </div>
            </div>
          </motion.article>
        </div>
      </div>

      {/* Checkout Modal */}
      <AnimatePresence data-id="element-981">
        {selectedEvent && <CheckoutModal event={selectedEvent} onClose={() => setSelectedEvent(null)} data-id="element-982" />}
      </AnimatePresence>
    </section>;
}